import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Row, Col, Container, Card, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaClipboardList, 
  FaCheckCircle,
  FaAward,
  FaFileAlt
} from 'react-icons/fa';

function ApprenticeshipExam() {
  const [formData, setFormData] = useState({
    profession: '',
    examType: 'cech',
    chamber: '',
    email: '',
    address: '',
    invoiceType: 'individual', // 'individual' lub 'company'
    firstName: '',
    lastName: '',
    companyName: '',
    nip: '',
    companyAddress: ''
  });

  const [paperVersion, setPaperVersion] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Sprawdzaj status płatności co 3 sekundy jeśli formularz został wysłany
  useEffect(() => {
    if (!submitted || !orderId || paymentCompleted) return;

    const checkPaymentInterval = setInterval(() => {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://sigma-work-api.onrender.com/api';
      const baseUrl = apiUrl.endsWith('/api') ? apiUrl : apiUrl.replace(/\/$/, '') + '/api';
      
      fetch(`${baseUrl}/payment/apprenticeship-exam/check/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId })
      })
      .then(response => response.json())
      .then(data => {
        if (data.trackPixel?.enabled) {
          window.ensureTTQ().then(() => {
            try {
              window.ttq.track('Purchase', {
                value: data.trackPixel.value,
                currency: data.trackPixel.currency,
                content_id: data.trackPixel.orderId,
                content_type: 'exam'
              });
              console.log('✅ TikTok pixel tracked');
            } catch (e) {
              console.warn('⚠️ TikTok pixel error:', e);
            }
          });
        }      
        if (data.status === 'completed') {
          setPaymentCompleted(true);
          clearInterval(checkPaymentInterval);
        }
      })
      .catch(error => console.error('Błąd sprawdzania statusu:', error));
    }, 3000);

    return () => clearInterval(checkPaymentInterval);
  }, [submitted, orderId, paymentCompleted]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Wysyłanie danych na backend
    const apiUrl = process.env.REACT_APP_API_URL || 'https://sigma-work-api.onrender.com/api';
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl : apiUrl.replace(/\/$/, '') + '/api';
    
    fetch(`${baseUrl}/payment/apprenticeship-exam/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      if (data.orderId) {
        setOrderId(data.orderId);
        setSubmitted(true);
        // Przejdź do PayU po 2 sekundach
        setTimeout(() => {
          if (data.redirectUri) {
            window.location.href = data.redirectUri;
          }
        }, 2000);
      } else if (data.error) {
        alert('Błąd: ' + data.error);
      }
    })
    .catch(error => {
      console.error('Błąd:', error);
      alert('Błąd przy przesyłaniu formularza. Spróbuj ponownie.');
    });
  };

  const features = [
    {
      icon: <FaClipboardList size={40} />,
      title: "Kompleksowe materiały",
      description: "Przygotuj się do egzaminu z profesjonalnymi materiałami szkoleniowymi"
    },
    {
      icon: <FaAward size={40} />,
      title: "Wersja papierowa",
      description: "Materiały przesłane na Twój adres w wersji drukowanej"
    },
    {
      icon: <FaFileAlt size={40} />,
      title: "Faktury",
      description: "Wszystkie dokumenty finansowe przygotowane dla Ciebie"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Egzamin czeladniczy – SIGMA | Przygotowanie do egzaminu zawodowego</title>
        <meta name="description" content="Przygotuj się do egzaminu czeladniczego. Materiały edukacyjne, wsparcie i faktury dla uczestników egzaminów zawodowych." />
        <link rel="canonical" href="https://sigmawork.pl/exam" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Egzamin czeladniczy – SIGMA" />
        <meta property="og:description" content="Przygotowanie do egzaminu czeladniczego" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sigmawork.pl/exam" />
      </Helmet>

      <div style={{
        minHeight: '100vh',
        paddingTop: '4rem',
        paddingBottom: '4rem',
        overflowX: 'clip',
        width: '100%'
      }}>
        <Container fluid className="px-2 px-md-4">

          {/* ===== HEADER ===== */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-4"
          >
            <h1 className="text-white fw-bold" style={{ fontSize: '3rem' }}>
              Egzamin czeladniczy
            </h1>
            <p className="text-white-50" style={{ fontSize: '1.3rem' }}>
              Przygotuj się do egzaminu z profesjonalnymi materiałami
            </p>
          </motion.div>

          {/* ===== MAIN CONTENT ===== */}
          <Row className="g-4 align-items-start">
            {/* FORMULARZ */}
            <Col xs={12} lg={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Card 
                  className="border-0 shadow-lg"
                  style={{
                    borderRadius: '25px',
                    overflow: 'hidden',
                  }}
                >
                  <Card.Body className="p-4 p-md-5">
                    <h2 className="fw-bold mb-4">Zamówienie opracowania materiałów egzaminacyjnych</h2>
                    
                    {submitted ? (
                      paymentCompleted ? (
                        <div style={{
                          background: 'rgba(67, 233, 123, 0.1)',
                          border: '2px solid rgba(67, 233, 123, 0.5)',
                          borderRadius: '15px',
                          padding: '2rem',
                          textAlign: 'center'
                        }}>
                          <FaCheckCircle size={60} style={{ color: '#43e97b', marginBottom: '1rem' }} />
                          <h3 className="text-white fw-bold mb-3">🎉 Płatność potwierdzona!</h3>
                          <p className="text-white mb-2">Dziękujemy za zakup materiałów do egzaminu czeladniczego.</p>
                          
                          <div style={{
                            background: 'rgba(67, 233, 123, 0.2)',
                            border: '1px solid rgba(67, 233, 123, 0.3)',
                            borderRadius: '10px',
                            padding: '1.5rem',
                            margin: '1.5rem 0',
                            textAlign: 'left'
                          }}>
                            <p style={{ fontSize: '0.95rem', color: '#d0d0d0', marginBottom: '0.5rem' }}>
                              <strong>⏰ Ważne informacje:</strong>
                            </p>
                            <ul style={{ fontSize: '0.9rem', color: '#b8b8b8', marginBottom: '0', paddingLeft: '1.5rem' }}>
                              <li>Materiały przygotowawcze zostaną wysłane na email <strong className="text-white">{formData.email}</strong> w ciągu <strong>48 godzin</strong></li>
                              <li>Jeśli wybrałeś wersję papierową, otrzymasz ją w ciągu <strong>7 dni roboczych</strong></li>
                              <li>Sprawdzaj folder SPAM, aby nie przegapić emaila z materiałami</li>
                            </ul>
                          </div>
                          
                          <p className="text-white-50 mb-2">
                            Numer zamówienia: <span className="text-white fw-bold">{orderId}</span>
                          </p>
                          <p className="text-white-50 mb-3">
                            Email potwierdzenia: <span className="text-white fw-bold">{formData.email}</span>
                          </p>
                          
                          <button
                            style={{
                              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                              border: 'none',
                              color: 'white',
                              padding: '0.75rem 2rem',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              marginTop: '1.5rem'
                            }}
                            onClick={() => window.location.href = '/'}
                          >
                            Powrót do strony głównej
                          </button>
                        </div>
                      ) : (
                        <div style={{
                          background: 'rgba(79, 172, 254, 0.1)',
                          border: '2px solid rgba(79, 172, 254, 0.5)',
                          borderRadius: '15px',
                          padding: '2rem',
                          textAlign: 'center'
                        }}>
                          <FaCheckCircle size={60} style={{ color: '#4FACFE', marginBottom: '1rem' }} />
                          <h3 className="text-white fw-bold mb-2">Formularz wysłany!</h3>
                          <p className="text-white mb-3">Twoje dane zostały zarejestrowane. Teraz musisz opłacić zamówienie.</p>
                          <p className="text-white-50 mb-3">
                            Numer zamówienia: <span className="text-white fw-bold">{orderId}</span>
                          </p>
                          <p className="text-white mb-4">Po opłaceniu materiały zostaną wysłane na Twojego email'a.</p>
                          <div style={{ fontSize: '0.9rem', color: '#999' }}>
                            Oczekiwanie na potwierdzenie płatności...
                          </div>
                          <button
                            style={{
                              background: 'linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%)',
                              border: 'none',
                              color: 'white',
                              padding: '0.75rem 2rem',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              marginTop: '1.5rem'
                            }}
                            onClick={() => window.history.back()}
                          >
                            Zamknij
                          </button>
                        </div>
                      )
                    ) : (
                      <Form onSubmit={handleSubmit}>
                        {/* Zawód */}
                        <Form.Group className="mb-4">
                          <Form.Label className="text-white fw-bold">1. Zawód:</Form.Label>
                          <Form.Control
                            type="text"
                            name="profession"
                            value={formData.profession}
                            onChange={handleChange}
                            required
                            placeholder="Np. elektryk, stolarz, fryzjer"
                            style={{
                              borderRadius: '10px',
                              padding: '0.75rem',
                              border: '2px solid rgba(255,255,255,0.2)',
                              background: 'rgba(255,255,255,0.05)',
                              color: 'white'
                            }}
                          />
                        </Form.Group>

                        {/* Typ egzaminu */}
                        <Form.Group className="mb-4">
                          <Form.Label className="text-white fw-bold">2. Egzamin:</Form.Label>
                          <div>
                            <Form.Check
                              type="radio"
                              id="cech"
                              name="examType"
                              value="cech"
                              checked={formData.examType === 'cech'}
                              onChange={handleChange}
                              label="Cech"
                              className="text-white mb-2"
                              style={{ cursor: 'pointer' }}
                            />
                            {/* OKE ukryte tymczasowo */}
                            <Form.Check
                              type="radio"
                              id="oke"
                              name="examType"
                              value="oke"
                              checked={formData.examType === 'oke'}
                              onChange={handleChange}
                              label="OKE (Okręgowa Komisja Egzaminacyjna)"
                              className="text-white"
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        </Form.Group>

                        {/* Izba Rzemieślnicza */}
                        <Form.Group className="mb-4">
                          <Form.Label className="text-white fw-bold">3. Izba Rzemieślnicza (miasto):</Form.Label>
                          <Form.Control
                            type="text"
                            name="chamber"
                            value={formData.chamber}
                            onChange={handleChange}
                            required
                            placeholder="Np. Warszawa, Kraków, Poznań"
                            style={{
                              borderRadius: '10px',
                              padding: '0.75rem',
                              border: '2px solid rgba(255,255,255,0.2)',
                              background: 'rgba(255,255,255,0.05)',
                              color: 'white'
                            }}
                          />
                        </Form.Group>

                        {/* Email */}
                        <Form.Group className="mb-4">
                          <Form.Label className="text-white fw-bold">4. Email (do przesłania materiału):</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="twoj@email.com"
                            style={{
                              borderRadius: '10px',
                              padding: '0.75rem',
                              border: '2px solid rgba(255,255,255,0.2)',
                              background: 'rgba(255,255,255,0.05)',
                              color: 'white'
                            }}
                          />
                        </Form.Group>

                        {/* Checkbox - Wersja papierowa */}
                        <Form.Group className="mb-4">
                          <Form.Check
                            type="checkbox"
                            id="paperVersion"
                            checked={paperVersion}
                            onChange={(e) => {
                              setPaperVersion(e.target.checked);
                              if (!e.target.checked) {
                                setFormData(prev => ({ ...prev, address: '' }));
                              }
                            }}
                            label={
                              <span className="text-white fw-bold">
                                5. Chcę otrzymać wersję papierową {paperVersion ? '(+30 zł)' : ''}
                              </span>
                            }
                            style={{ cursor: 'pointer' }}
                          />
                          <small className="text-white-50 d-block mt-2">
                            Materiały będą wysłane na Twój adres w ciągu 7 dni roboczych
                          </small>
                        </Form.Group>

                        {/* Adres do doręczenia - widoczny tylko gdy zaznaczony checkbox */}
                        {paperVersion && (
                          <Form.Group className="mb-4">
                            <Form.Label className="text-white fw-bold">Adres do doręczenia:</Form.Label>
                            <Form.Control
                              as="textarea"
                              name="address"
                              value={formData.address}
                              onChange={handleChange}
                              required={paperVersion}
                              rows={3}
                              placeholder="Ulica, numer domu, kod pocztowy, miasto"
                              style={{
                                borderRadius: '10px',
                                padding: '0.75rem',
                                border: '2px solid rgba(255,255,255,0.2)',
                                background: 'rgba(255,255,255,0.05)',
                                color: 'white',
                                resize: 'none'
                              }}
                            />
                          </Form.Group>
                        )}

                        {/* Dane do faktury */}
                        <Form.Group className="mb-4">
                          <Form.Label className="text-white fw-bold">6. Dane do faktury:</Form.Label>
                          
                          {/* Wybór: Osoba fizyczna / Firma */}
                          <div className="mb-3">
                            <Form.Check
                              type="radio"
                              id="invoiceIndividual"
                              name="invoiceType"
                              value="individual"
                              checked={formData.invoiceType === 'individual'}
                              onChange={handleChange}
                              label="Osoba fizyczna"
                              className="text-white mb-2"
                              style={{ cursor: 'pointer' }}
                            />
                            <Form.Check
                              type="radio"
                              id="invoiceCompany"
                              name="invoiceType"
                              value="company"
                              checked={formData.invoiceType === 'company'}
                              onChange={handleChange}
                              label="Firma"
                              className="text-white"
                              style={{ cursor: 'pointer' }}
                            />
                          </div>

                          {/* Pola dla osoby fizycznej */}
                          {formData.invoiceType === 'individual' && (
                            <>
                              <Form.Control
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                placeholder="Imię"
                                className="mb-3"
                                style={{
                                  borderRadius: '10px',
                                  padding: '0.75rem',
                                  border: '2px solid rgba(255,255,255,0.2)',
                                  background: 'rgba(255,255,255,0.05)',
                                  color: 'white'
                                }}
                              />
                              <Form.Control
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                                placeholder="Nazwisko"
                                style={{
                                  borderRadius: '10px',
                                  padding: '0.75rem',
                                  border: '2px solid rgba(255,255,255,0.2)',
                                  background: 'rgba(255,255,255,0.05)',
                                  color: 'white'
                                }}
                              />
                            </>
                          )}

                          {/* Pola dla firmy */}
                          {formData.invoiceType === 'company' && (
                            <>
                              <Form.Control
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                required
                                placeholder="Nazwa firmy"
                                className="mb-3"
                                style={{
                                  borderRadius: '10px',
                                  padding: '0.75rem',
                                  border: '2px solid rgba(255,255,255,0.2)',
                                  background: 'rgba(255,255,255,0.05)',
                                  color: 'white'
                                }}
                              />
                              <Form.Control
                                type="text"
                                name="nip"
                                value={formData.nip}
                                onChange={handleChange}
                                required
                                placeholder="NIP"
                                className="mb-3"
                                style={{
                                  borderRadius: '10px',
                                  padding: '0.75rem',
                                  border: '2px solid rgba(255,255,255,0.2)',
                                  background: 'rgba(255,255,255,0.05)',
                                  color: 'white'
                                }}
                              />
                              <Form.Control
                                type="text"
                                name="companyAddress"
                                value={formData.companyAddress}
                                onChange={handleChange}
                                required
                                placeholder="Adres firmy"
                                style={{
                                  borderRadius: '10px',
                                  padding: '0.75rem',
                                  border: '2px solid rgba(255,255,255,0.2)',
                                  background: 'rgba(255,255,255,0.05)',
                                  color: 'white'
                                }}
                              />
                            </>
                          )}
                        </Form.Group>

                        {/* Przycisk Wyślij */}
                        <button
                          type="submit"
                          style={{
                            width: '100%',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '10px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                            color: 'white',
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                          onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                        >
                          Wyślij
                        </button>
                      </Form>
                    )}
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            {/* INFORMACJE */}
            <Col xs={12} lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Info Box */}
                <Card 
                  className="border-0 shadow-lg mb-4"
                  style={{
                    borderRadius: '25px',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1) 0%, rgba(56, 249, 215, 0.1) 100%)'
                  }}
                >
                  <Card.Body className="p-4">
                    <h3 className="text-white fw-bold mb-3">Co otrzymasz?</h3>
                    <ul className="text-white list-unstyled" style={{ fontSize: '1rem', lineHeight: '2' }}>
                      <li><FaCheckCircle className="me-2" style={{ color: '#43e97b' }} /><strong>Wersja elektroniczna:</strong> 270 PLN - materiały na email</li>
                      <li><FaCheckCircle className="me-2" style={{ color: '#43e97b' }} /><strong>Wersja papierowa:</strong> 300 PLN - materiały + przesyłka</li>
                      <li><FaCheckCircle className="me-2" style={{ color: '#43e97b' }} />Dostarczenie w ciągu 5 minut</li>
                      <li><FaCheckCircle className="me-2" style={{ color: '#43e97b' }} />Fakturę ze wskazanymi danymi</li>
                      <li><FaCheckCircle className="me-2" style={{ color: '#43e97b' }} />Personalne wsparcie</li>
                    </ul>
                    <p className="text-white-50 mb-0 mt-3" style={{ fontSize: '0.85rem' }}>
                      <strong>Ważne:</strong> Jeśli wybierasz wersję papierową, pamiętaj o podaniu adresu do wysyłki.
                    </p>
                  </Card.Body>
                </Card>

                {/* Features */}
                <Row className="g-3">
                  {features.map((feature, idx) => (
                    <Col key={idx} xs={12}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                      >
                        <Card 
                          className="border-0 shadow"
                          style={{
                            borderRadius: '15px',
                            background: 'rgba(255,255,255,0.05)',
                            backdropFilter: 'blur(10px)'
                          }}
                        >
                          <Card.Body className="p-3">
                            <div className="d-flex align-items-start">
                              <div style={{ color: '#43e97b', marginRight: '1rem', fontSize: '1.5rem' }}>
                                {feature.icon}
                              </div>
                              <div>
                                <h5 className="text-white fw-bold mb-1">{feature.title}</h5>
                                <p className="text-white-50 mb-0" style={{ fontSize: '0.9rem' }}>
                                  {feature.description}
                                </p>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </motion.div>
            </Col>
          </Row>

          <hr className="my-5" style={{ borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* INFORMACJE O EGZAMINIE */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="my-5 py-5"
          >
            <h2 className="text-center text-white fw-bold mb-5" style={{ fontSize: '2.5rem' }}>
              Czym jest egzamin czeladniczy?
            </h2>
            <Row className="g-4">
              <Col xs={12} md={6}>
                <Card 
                  className="border-0 shadow h-100"
                  style={{
                    borderRadius: '20px',
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Card.Body className="p-4">
                    <h4 className="text-white fw-bold mb-3">
                      <FaAward className="me-2" style={{ color: '#43e97b' }} />
                      Znaczenie
                    </h4>
                    <p className="text-white mb-0">
                      Egzamin czeladniczy potwierdza opanowanie praktycznych umiejętności zawodowych. Jest wymagany dla osób pracujących w zawodach rzemieślniczych.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card 
                  className="border-0 shadow h-100"
                  style={{
                    borderRadius: '20px',
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Card.Body className="p-4">
                    <h4 className="text-white fw-bold mb-3">
                      <FaClipboardList className="me-2" style={{ color: '#43e97b' }} />
                      Przygotowanie
                    </h4>
                    <p className="text-white mb-0">
                      Prześlemy Ci profesjonalne materiały przygotowawcze do egzaminu czeladniczego. Dostępne są w wersji elektronicznej (270 PLN) oraz papierowej z wysyłką (300 PLN). Materiały dostarczane są w ciągu 5 minut od opłacenia zamówienia.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </motion.div>

        </Container>
      </div>
    </>
  );
}

export default ApprenticeshipExam;
